import { api } from "@/api/client";
import {
  type ReceiptKind,
  type TransferItemView,
  type TransferResolution,
  type TransferScanApi,
  type TransferSessionHeader,
  transferScanApi,
} from "@/api/transfer_check";

/**
 * Двойное подтверждение перемещения (контракт этапов 2–3 в
 * `docs/todo/2026-10-28-transfer-two-step.md` §4): «Приёмка перемещений»
 * (`/api/transfer-receipts`, право `transfer_receive`) и «Расхождения
 * перемещений» (`/api/transfer-discrepancies`, право `transfer_discrepancy`).
 * Ошибки — `{"detail": "<текст>"}`; 403 без привязки к складу — текстом сервера.
 */

/** Статус в списках приёмки и расхождений. */
export type ReceiptStatus = "sent" | "discrepancy" | "completed";

export type { ReceiptKind } from "@/api/transfer_check";

export interface ReceiptCard {
  session_id: string;
  /** `order` — по заказу (проверка перемещений), `free` — «Перемещение» без
   *  заказа: `transfer_number` пуст, номер — `display_number` (`receiptTitle`). */
  kind: ReceiptKind;
  display_number: number | null;
  transfer_number: string;
  organization_name: string | null;
  from_wh_name: string | null;
  to_wh_name: string | null;
  /** ISO; когда и кто (логин) отправил. */
  sent_at: string | null;
  sent_by_name: string | null;
  status: ReceiptStatus;
  /** Сколько позиций и сколько всего (базовые единицы) отправлено. */
  positions: number;
  qty_total: number;
  received_at: string | null;
  transfer_doc_number: string | null;
  /** Как разобрано: `match` — принято без расхождений, `by_receipt` — по
   *  принятому после разбора; null — не решено. */
  resolution: TransferResolution | null;
  /** Проведён ли документ 1С (у `completed` бывает `false` — проведение не
   *  дошло, повтор действия доделывает). */
  transfer_doc_posted: boolean;
}

export interface ReceiptList {
  items: ReceiptCard[];
}

/** Строка сравнения по ключу (номенклатура, характеристика) — партии внутри
 *  строки сведены; `diff = received − sent`, базовые единицы. `partia_name`
 *  может быть списком («211, 212»); `partia_mismatch` — партии принятого не те,
 *  что отправлены (даже при `diff = 0`), `partia_note` — чем именно («принято
 *  партии 47 — 5, отправлено 0»). Такая строка — тоже расхождение (`matches`). */
export interface CompareRow {
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  sent_qty: number;
  received_qty: number;
  diff: number;
  /** Имя базовой единицы номенклатуры для подписи количеств; пусто/нет —
   *  число без слова (единицу наугад не подставляем). */
  base_unit_name?: string;
  partia_mismatch: boolean;
  partia_note: string;
}

export interface ReceiptDetail {
  header: TransferSessionHeader;
  /** Строки отправителя — как отправлено, не меняются. */
  sent: TransferItemView[];
  /** Строки получателя. */
  received: TransferItemView[];
  comparison: CompareRow[];
  matches: boolean;
}

export interface AcceptResult {
  header: TransferSessionHeader;
  outcome: "completed" | "discrepancy";
  comparison: CompareRow[];
  matches: boolean;
  transfer_doc_number: string | null;
  posted: boolean;
}

// ── Приёмка ──────────────────────────────────────────────────────────────

export async function listReceipts(): Promise<ReceiptList> {
  const { data } = await api.get<ReceiptList>("/transfer-receipts");
  return data;
}

export async function getReceipt(id: string): Promise<ReceiptDetail> {
  const { data } = await api.get<ReceiptDetail>(`/transfer-receipts/${id}`);
  return data;
}

/** Скан-ручки строк получателя — те же, что у отправителя, по своему адресу.
 *  Остаток склада бэк здесь не проверяет (товар в пути). */
export function receiptScanApi(id: string): TransferScanApi {
  return transferScanApi(`/transfer-receipts/${id}`);
}

/** «Принять»: совпало — заказ под факт + документ + проведение; нет —
 *  расхождение, в 1С ничего. 502 — отказ проведения (текст 1С), повтор
 *  доделывает без второго документа. У сессии, решённой «по принятому», —
 *  409 «Документ по принятому проводит разбор расхождений.» */
export async function acceptReceipt(id: string): Promise<AcceptResult> {
  const { data } = await api.post<AcceptResult>(`/transfer-receipts/${id}/accept`);
  return data;
}

/** Конфиг приёмки для экрана скана: способ скана — то же значение, что
 *  отправитель берёт из `/order-check/config` (у получателя этого права нет). */
export interface ReceiptConfig {
  scan_method: string;
}

export async function getReceiptConfig(): Promise<ReceiptConfig> {
  const { data } = await api.get<ReceiptConfig>("/transfer-receipts/config");
  return data;
}

// ── Расхождения ──────────────────────────────────────────────────────────

/** `discrepancy`, недоведённые «по принятому» (решение сохранено, документ
 *  не проведён — повтор `resolve-by-receipt` доделывает) и застрявшие
 *  «Принять» (`resolution=match`, не проведено — «Сбросить приёмку»/«Отменить»). */
export async function listDiscrepancies(): Promise<ReceiptList> {
  const { data } = await api.get<ReceiptList>("/transfer-discrepancies");
  return data;
}

export async function getDiscrepancy(id: string): Promise<ReceiptDetail> {
  const { data } = await api.get<ReceiptDetail>(`/transfer-discrepancies/${id}`);
  return data;
}

/** Вернуть приёмку получателю (`status=sent`), его строки сохраняются. */
export async function recountDiscrepancy(id: string): Promise<TransferSessionHeader> {
  const { data } = await api.post<TransferSessionHeader>(
    `/transfer-discrepancies/${id}/recount`,
  );
  return data;
}

/** Провести по принятому: факт = строки получателя. 502 — отказ проведения. */
export async function resolveDiscrepancyByReceipt(id: string): Promise<AcceptResult> {
  const { data } = await api.post<AcceptResult>(
    `/transfer-discrepancies/${id}/resolve-by-receipt`,
  );
  return data;
}

/** Отменить: `cancelled`, записанное в 1С (документ, правка заказа) —
 *  откатывается, заказ снова можно собирать новой проверкой. 502 — 1С не дала
 *  откатить шаг; повтор доделывает. */
export async function cancelDiscrepancy(id: string): Promise<TransferSessionHeader> {
  const { data } = await api.post<TransferSessionHeader>(
    `/transfer-discrepancies/${id}/cancel`,
  );
  return data;
}

/** Сбросить застрявшее «Принять»: документ и правка заказа в 1С отменяются,
 *  приёмка возвращается получателю (`sent`, строки сохраняются). 502 — 1С не
 *  дала откатить шаг; повтор доделывает. */
export async function resetReceipt(id: string): Promise<TransferSessionHeader> {
  const { data } = await api.post<TransferSessionHeader>(
    `/transfer-discrepancies/${id}/reset`,
  );
  return data;
}

// ── Привязки принимающих (админ) ─────────────────────────────────────────

export interface WarehouseRef {
  ref: string;
  name: string;
}

export interface ReceiverBinding {
  user_id: number;
  username: string;
  full_name: string | null;
  warehouses: WarehouseRef[];
}

/** Только пользователи с привязками. */
export async function listReceiverBindings(): Promise<ReceiverBinding[]> {
  const { data } = await api.get<ReceiverBinding[]>("/transfer-receipts/admin/bindings");
  return data;
}

/** Задать склады пользователя целиком; `[]` — снять все. */
export async function setReceiverBinding(
  userId: number,
  warehouses: WarehouseRef[],
): Promise<ReceiverBinding> {
  const { data } = await api.put<ReceiverBinding>(
    `/transfer-receipts/admin/bindings/${userId}`,
    { warehouses },
  );
  return data;
}

/** Ответ 502 — 1С отказала посреди действия (проведение; откат при
 *  «Отменить»/«Сбросить приёмку»): сделанное отмечено, повтор доделывает. */
export function isPostRefusal(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 502;
}
